<script lang="ts" setup>
import { arrow as floatingArrow, autoPlacement, autoUpdate, flip, offset, shift, size, useFloating, type Placement as FloatingPlacement } from '@floating-ui/vue';
import { PLACEMENTS, type Placement, type VirtualReference } from './types';

const instance = getCurrentInstance();

defineOptions({
    name: 'IdeoPopover',
    inheritAttrs: false
});

const props = withDefaults(defineProps<{
    title?: string;
    triggers?: string;
    boundary?: string;
    target: any;
    placement?: Placement;
    variant?: 'popover' | 'tooltip' | 'confirmation' | 'menu';
    distance?: number;
    noFlip?: boolean;
    arrow?: boolean;
    body?: boolean;
    closeOnGlobalOpen?: boolean;
    captureHover?: boolean;
    lazy?: boolean;
    matchTargetWidth?: boolean;
    shiftCrossAxis?: boolean;
    hoverCloseDelay?: number;
    maxWidth?: number;
}>(), {
    title: null,
    triggers: 'click',
    boundary: null,
    placement: 'right',
    variant: 'popover',
    distance: 8,
    noFlip: false,
    arrow: true,
    body: true,
    closeOnGlobalOpen: true,
    captureHover: true,
    lazy: true,
    matchTargetWidth: false,
    shiftCrossAxis: false,
    hoverCloseDelay: 80,
    maxWidth: undefined
});

const emit = defineEmits<{
    (e: 'close'): void;
}>();

const attrs = useAttrs() as any;
const reference = ref<HTMLElement | VirtualReference | null>(null);
const popover = ref<HTMLElement | null>(null);
const arrowRef = ref<HTMLElement | null>(null);
const rendered = ref(!props.lazy);
const visible = ref(false);
const immediateClose = ref(false);
const currentTarget = ref<HTMLElement | null>(null);
const floatingAvailableWidth = ref<number | null>(null);
const floatingReferenceWidth = ref<number | null>(null);
const closeAnimationFallbackDelay = computed(() => props.variant === 'menu' ? 255 : 220);
const arrowSize = 10;
const floatingBoundaryPadding = 12;

let closeAnimationTimeout: number | null = null;
let closeAnimationCleanup: (() => void) | null = null;
let openAnimationFrame: number | null = null;
let openAnimationVersion = 0;
let openRequested = false;
let disposed = false;
let hoverCloseTimeout: number | null = null;
let menuContentAnimations: Animation[] = [];
let pointerDownInsidePopover = false;
let pointerDownInsidePopoverTimeout: number | null = null;
let closeWhenReferenceUnavailable: (() => void) | null = null;

const resolvedPlacement = computed(() => PLACEMENTS[props.placement]);
const resolvedTriggers = computed(() => props.triggers.toLowerCase().trim().split(/\s+/).filter(Boolean).sort());

const floatingPlacement = computed(() =>
{
    if (resolvedPlacement.value.popover === 'auto')
    {
        return 'bottom';
    }

    let placement = resolvedPlacement.value.popover;

    switch (resolvedPlacement.value.arrow)
    {
        case 'top':
        case 'left':
            placement = `${placement}-start`;
            break;
        case 'bottom':
        case 'right':
            placement = `${placement}-end`;
            break;
    }

    return placement as FloatingPlacement;
});

const overflowOptions = computed(() =>
{
    const boundarySelector = props.boundary === 'body' ? null : props.boundary;
    const boundary = boundarySelector && typeof document !== 'undefined'
        ? document.querySelector(boundarySelector)
        : null;

    return boundary
        ? { boundary, padding: floatingBoundaryPadding }
        : { padding: floatingBoundaryPadding };
});

const middleware = computed(() =>
{
    const items: any[] = [
        offset(props.distance)
    ];

    if (resolvedPlacement.value.popover === 'auto')
    {
        items.push(autoPlacement(overflowOptions.value));
    }
    else if (!props.noFlip)
    {
        items.push(flip(overflowOptions.value));
    }

    // shift musi być przed size — inaczej availableWidth liczy się w pozycji
    // niedosuniętej i przy krawędzi viewportu tooltip łamie tekst litera po literze
    items.push(shift({
        ...overflowOptions.value,
        crossAxis: props.shiftCrossAxis
    }));
    items.push(size({
        ...overflowOptions.value,
        apply({ availableWidth, rects })
        {
            floatingAvailableWidth.value = Math.max(0, Math.floor(availableWidth));
            floatingReferenceWidth.value = Math.max(0, Math.floor(rects.reference.width));
        }
    }));

    if (props.arrow)
    {
        items.push(floatingArrow({ element: arrowRef, padding: 8 }));
    }

    return items;
});

const { floatingStyles, middlewareData, placement: floatingResolvedPlacement, update } = useFloating(reference, popover, {
    placement: floatingPlacement,
    middleware,
    open: rendered,
    transform: false,
    whileElementsMounted: startFloatingAutoUpdate
});

const popoverPlacement = computed(() => floatingResolvedPlacement.value.split('-')[0]);

const ignoredFloatingClassNames = new Set([
    'arrow',
    'popover',
    'popover-arrow',
    'popover-body',
    'popover-header',
    'shadow'
]);

function isIgnoredFloatingClassName(name: string)
{
    return ignoredFloatingClassNames.has(name);
}

const normalizeClassValue = (value: any) =>
{
    if (!value)
    {
        return {};
    }

    if (Array.isArray(value))
    {
        return value.reduce<Record<string, boolean>>((classes, item) => ({
            ...classes,
            ...normalizeClassValue(item)
        }), {});
    }

    if (typeof value == 'object')
    {
        return Object.entries(value).reduce<Record<string, boolean>>((classes, [name, enabled]) =>
        {
            if (!enabled)
            {
                return classes;
            }

            return {
                ...classes,
                ...normalizeClassValue(name)
            };
        }, {});
    }

    return String(value)
        .split(/\s+/)
        .filter(name => !!name && !isIgnoredFloatingClassName(name))
        .reduce<Record<string, boolean>>((classes, name) => ({
            ...classes,
            [name]: true
        }), {});
};

const floatingAttrClasses = computed(() => normalizeClassValue(attrs.class));

const forwardedAttrs = computed(() =>
{
    const rest = { ...attrs };

    delete rest.class;

    return rest;
});

const fallbackAvailableWidth = () =>
{
    if (typeof window === 'undefined')
    {
        return floatingMaxWidth.value;
    }

    return Math.max(0, window.innerWidth - (floatingBoundaryPadding * 2));
};

const floatingMaxWidth = computed(() =>
{
    if (props.maxWidth)
        return props.maxWidth;

    if (props.variant === 'tooltip')
    {
        return floatingAttrClasses.value['ideo-floating--multiline'] ? 288 : 320;
    }

    if (props.variant === 'confirmation')
    {
        return 464;
    }

    if (props.variant === 'menu')
    {
        return 480;
    }

    return 360;
});

const resolvedMaxWidth = computed(() =>
{
    const width = floatingAvailableWidth.value ?? fallbackAvailableWidth();

    return `${Math.min(floatingMaxWidth.value, width)}px`;
});

const popoverStyles = computed(() =>
{
    const styles = {
        ...floatingStyles.value,
        maxWidth: resolvedMaxWidth.value
    };

    if (props.matchTargetWidth && floatingReferenceWidth.value !== null)
    {
        const availableWidth = floatingAvailableWidth.value ?? fallbackAvailableWidth();
        const width = Math.min(floatingReferenceWidth.value, availableWidth);

        return {
            ...styles,
            width: `${width}px`,
            minWidth: `${width}px`,
            maxWidth: `${width}px`
        };
    }

    return styles;
});

const variantClasses = computed(() => ({
    popover: 'border border-border bg-surface px-[10px] py-[8px] text-fg shadow-lg dark:border-border-dark dark:bg-surface-dark dark:text-fg-dark',
    tooltip: 'border-0 bg-floating px-[8px] py-[6px] text-white shadow-[0_8px_24px_-8px_oklch(0_0_0/0.45)] dark:bg-floating-dark',
    confirmation: 'border border-border bg-surface p-[4px] text-fg shadow-lg dark:border-border-dark dark:bg-surface-dark dark:text-fg-dark',
    menu: 'min-w-[min(150px,calc(100vw-24px))] border border-border bg-surface p-[4px] text-fg shadow-lg dark:border-border-dark dark:bg-surface-dark dark:text-fg-dark'
}[props.variant]));

const motionClasses = computed(() =>
{
    if (props.variant === 'menu')
    {
        return visible.value
            ? '[transition:opacity_0.14s_cubic-bezier(.2,.8,.3,1),scale_0.105s_cubic-bezier(.2,.8,.3,1)]'
            : '[transition:opacity_0.14s_cubic-bezier(.4,0,1,1)_0.035s,scale_0.098s_cubic-bezier(.4,0,1,1)]';
    }

    return '[transition:opacity_0.1s_ease,scale_0.1s_ease]';
});

const bridgeClasses = computed(() => ({
    bottom: 'before:inset-x-[-8px] before:top-[-14px] before:h-[14px]',
    left: 'before:inset-y-[-8px] before:end-[-14px] before:w-[14px]',
    right: 'before:inset-y-[-8px] before:start-[-14px] before:w-[14px]',
    top: 'before:inset-x-[-8px] before:bottom-[-14px] before:h-[14px]'
}[popoverPlacement.value]));

const popoverClasses = computed(() => [
    'ideo-floating isolate z-4 box-border w-max origin-center overflow-visible rounded-[8px] text-[0.71875rem]/normal font-[400] before:absolute before:pointer-events-auto before:content-[\'\'] motion-reduce:transition-none',
    motionClasses.value,
    immediateClose.value ? '!hidden' : '',
    visible.value ? 'is-open pointer-events-auto scale-100 opacity-100' : 'pointer-events-none scale-[0.95] opacity-0',
    variantClasses.value,
    bridgeClasses.value,
    `ideo-floating--${props.variant}`,
    `ideo-floating--placement-${popoverPlacement.value}`,
    props.variant === 'menu' ? 'before:hidden' : '',
    props.captureHover ? '' : 'ideo-floating--pass-through-hover !pointer-events-none before:!pointer-events-none',
    floatingAttrClasses.value
]);

const arrowClasses = computed(() => [
    'ideo-floating-arrow absolute z-[-1] size-[10px] rotate-45 border border-border bg-surface dark:border-border-dark dark:bg-surface-dark',
    props.variant === 'tooltip' ? '!border-0 !bg-floating dark:!bg-floating-dark' : ''
]);

const arrowStyles = computed(() =>
{
    const arrowData = middlewareData.value.arrow;
    const side = floatingResolvedPlacement.value.split('-')[0];
    const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right'
    }[side] as string;
    const styles: Record<string, string> = {
        [staticSide]: `${arrowSize / -2}px`
    };

    if (arrowData?.x != null)
    {
        styles.left = `${arrowData.x}px`;
    }

    if (arrowData?.y != null)
    {
        styles.top = `${arrowData.y}px`;
    }

    return styles;
});

const targetElement = () =>
{
    const target = typeof props.target == 'string'
        ? document.getElementById(props.target)
        : unref(props.target);

    return target instanceof HTMLElement
        ? target
        : null;
};

const isReferenceAvailable = (element: HTMLElement | null) =>
{
    return !!element && element.isConnected && element.getClientRects().length > 0 && isReferenceVisible(element);
};

const isVirtualReference = (element: any): element is VirtualReference =>
{
    return !!element && !(element instanceof HTMLElement) && typeof element.getBoundingClientRect === 'function';
};

function isReferenceVisible(element: HTMLElement)
{
    if (typeof document === 'undefined' || typeof window === 'undefined')
    {
        return true;
    }

    const rect = element.getBoundingClientRect();
    const xOffset = Math.min(6, rect.width / 2);
    const yOffset = Math.min(6, rect.height / 2);
    const points = [
        [rect.left + (rect.width / 2), rect.top + (rect.height / 2)],
        [rect.left + xOffset, rect.top + yOffset],
        [rect.right - xOffset, rect.top + yOffset],
        [rect.left + xOffset, rect.bottom - yOffset],
        [rect.right - xOffset, rect.bottom - yOffset]
    ];

    return points.some(([x, y]) =>
    {
        if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight)
        {
            return false;
        }

        const hit = document.elementFromPoint(x, y);

        return !!hit && (element.contains(hit) || isOwnFloatingElement(hit));
    });
}

function isOwnFloatingElement(element: Element)
{
    return !!popover.value && popover.value.contains(element);
}

function startFloatingAutoUpdate(referenceElement: any, floatingElement: HTMLElement, updatePosition: () => void)
{
    const updateWhenAvailable = () =>
    {
        if (referenceElement instanceof HTMLElement && !isReferenceAvailable(referenceElement))
        {
            closeWhenReferenceUnavailable?.();

            return;
        }

        updatePosition();
    };

    return autoUpdate(referenceElement, floatingElement, updateWhenAvailable, {
        animationFrame: props.variant === 'confirmation' || props.variant === 'menu'
    });
}

const onGlobalOpen = (e: any) =>
{
    if (props.closeOnGlobalOpen && e.detail.component() != instance)
    {
        close();
    }
};

function isTransitionWithinPopoverBoundary(eventTarget: HTMLElement | null, relatedTarget: HTMLElement | null)
{
    if (!props.captureHover)
    {
        return false;
    }

    const tip = popover.value as HTMLElement | null;
    const target = targetElement();

    if (!eventTarget || !relatedTarget || !target)
    {
        return false;
    }

    return (
        (tip && tip.contains(eventTarget) && target.contains(relatedTarget)) ||
        (tip && target.contains(eventTarget) && tip.contains(relatedTarget)) ||
        (tip && tip.contains(eventTarget) && tip.contains(relatedTarget)) ||
        (target.contains(eventTarget) && target.contains(relatedTarget))
    );
}

const updatePosition = (element: HTMLElement | VirtualReference | null) =>
{
    reference.value = element;

    if (!isVirtualReference(element) && !isReferenceAvailable(element))
    {
        closeWhenReferenceUnavailable?.();

        return;
    }

    nextTick(() =>
    {
        update();
    });
};

function clearCloseAnimation()
{
    if (closeAnimationTimeout !== null)
    {
        window.clearTimeout(closeAnimationTimeout);
        closeAnimationTimeout = null;
    }

    closeAnimationCleanup?.();
    closeAnimationCleanup = null;
}

function clearOpenAnimation()
{
    openAnimationVersion++;

    if (openAnimationFrame !== null)
    {
        window.cancelAnimationFrame(openAnimationFrame);
        openAnimationFrame = null;
    }
}

function clearMenuContentAnimations()
{
    menuContentAnimations.forEach(animation => animation.cancel());
    menuContentAnimations = [];
}

function animateMenuContent(opening: boolean)
{
    if (props.variant !== 'menu' || !popover.value || window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    {
        clearMenuContentAnimations();

        return;
    }

    const elements = [...popover.value.children]
        .filter(element => !element.classList.contains('ideo-floating-arrow')) as HTMLElement[];
    const interrupted = menuContentAnimations.length > 0;
    const currentOpacity = new Map(elements.map(element => [element, Number.parseFloat(getComputedStyle(element).opacity) || 0]));

    clearMenuContentAnimations();

    const duration = 140;
    const delay = opening ? 35 : 0;
    const easing = opening ? 'cubic-bezier(.2, .8, .3, 1)' : 'cubic-bezier(.4, 0, 1, 1)';

    menuContentAnimations = elements.map((element) =>
    {
        const from = opening && !interrupted ? 0 : currentOpacity.get(element) ?? (opening ? 0 : 1);
        const animation = element.animate([
            { opacity: from },
            { opacity: opening ? 1 : 0 }
        ], {
            duration,
            delay,
            easing,
            fill: 'both'
        });

        if (opening)
        {
            animation.onfinish = () =>
            {
                animation.cancel();
                menuContentAnimations = menuContentAnimations.filter(item => item !== animation);
            };
        }

        return animation;
    });
}

function clearHoverClose()
{
    if (hoverCloseTimeout !== null)
    {
        window.clearTimeout(hoverCloseTimeout);
        hoverCloseTimeout = null;
    }
}

function clearPointerDownInsidePopover()
{
    if (pointerDownInsidePopoverTimeout !== null)
    {
        window.clearTimeout(pointerDownInsidePopoverTimeout);
        pointerDownInsidePopoverTimeout = null;
    }

    pointerDownInsidePopover = false;
}

function schedulePointerDownInsidePopoverClear()
{
    if (pointerDownInsidePopoverTimeout !== null)
    {
        window.clearTimeout(pointerDownInsidePopoverTimeout);
    }

    pointerDownInsidePopoverTimeout = window.setTimeout(clearPointerDownInsidePopover, 500);
}

function isEventWithinPopover(e: Event)
{
    const tip = popover.value as HTMLElement | null;

    return !!tip && e.composedPath().includes(tip);
}

function markPointerDownInsidePopover()
{
    if (pointerDownInsidePopoverTimeout !== null)
    {
        window.clearTimeout(pointerDownInsidePopoverTimeout);
        pointerDownInsidePopoverTimeout = null;
    }

    pointerDownInsidePopover = true;
    schedulePointerDownInsidePopoverClear();
}

function handleDocumentPointerDown(e: Event)
{
    if (isEventWithinPopover(e))
    {
        markPointerDownInsidePopover();

        return;
    }

    clearPointerDownInsidePopover();
}

function handlePopoverClick()
{
    schedulePointerDownInsidePopoverClear();
}

function scheduleHoverClose()
{
    clearHoverClose();

    hoverCloseTimeout = window.setTimeout(() =>
    {
        hoverCloseTimeout = null;
        close();
    }, props.hoverCloseDelay);
}

function finishClose()
{
    clearCloseAnimation();
    clearMenuContentAnimations();

    if (props.lazy)
    {
        rendered.value = false;
    }
}

function waitForCloseAnimation(currentPopover: HTMLElement | null)
{
    clearCloseAnimation();

    if (!currentPopover || window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    {
        finishClose();

        return;
    }

    const onTransitionEnd = (event: TransitionEvent) =>
    {
        if (event.target !== currentPopover || event.propertyName !== 'opacity')
        {
            return;
        }

        finishClose();
    };

    currentPopover.addEventListener('transitionend', onTransitionEnd);
    closeAnimationCleanup = () => currentPopover.removeEventListener('transitionend', onTransitionEnd);
    closeAnimationTimeout = window.setTimeout(finishClose, closeAnimationFallbackDelay.value);
}

const open = (target?: HTMLElement | VirtualReference) =>
{
    const element = target || targetElement();

    if (!element)
    {
        return;
    }

    clearHoverClose();
    clearCloseAnimation();
    clearOpenAnimation();
    floatingAvailableWidth.value = null;
    floatingReferenceWidth.value = null;
    immediateClose.value = false;
    rendered.value = true;
    openRequested = true;

    updatePosition(element);

    if (!openRequested)
        return;

    const animationVersion = openAnimationVersion;

    document.dispatchEvent(new CustomEvent('ideo-popover:open', {
        detail: { component: () => instance }
    }));

    nextTick(() =>
    {
        if (animationVersion !== openAnimationVersion || !rendered.value)
        {
            return;
        }

        openAnimationFrame = window.requestAnimationFrame(() =>
        {
            if (animationVersion !== openAnimationVersion || !rendered.value)
            {
                return;
            }

            openAnimationFrame = null;
            visible.value = true;
            animateMenuContent(true);

            if (resolvedTriggers.value.includes('hover') && props.captureHover)
            {
                popover.value?.addEventListener('mouseenter', clearHoverClose, { capture: false });
                popover.value?.addEventListener('mouseleave', handleEvent, { capture: false });
            }
        });
    });
};

const close = (options?: { immediate?: boolean }) =>
{
    const wasOpen = openRequested || visible.value;

    openRequested = false;
    clearOpenAnimation();
    if (options?.immediate) immediateClose.value = true;

    if (visible.value)
    {
        clearHoverClose();
        if (!immediateClose.value) animateMenuContent(false);

        if (resolvedTriggers.value.includes('hover') && props.captureHover)
        {
            popover.value?.removeEventListener('mouseleave', handleEvent, { capture: false });
            popover.value?.removeEventListener('mouseenter', clearHoverClose, { capture: false });
        }

        visible.value = false;

        if (immediateClose.value)
            finishClose();
        else
            waitForCloseAnimation(popover.value);
    }
    else if (props.lazy && rendered.value)
    {
        finishClose();
    }

    if (wasOpen)
        emit('close');
};

closeWhenReferenceUnavailable = close;

const toggle = (visible: boolean) =>
{
    if (visible)
        open();
    else
        close();
};

const handleEvent = (e: MouseEvent | FocusEvent) =>
{
    const type = e.type;
    const triggers = resolvedTriggers.value;

    if (type === 'click' && triggers.includes('click'))
    {
        toggle(true);
    }
    else if (type === 'mouseenter' && triggers.includes('hover'))
    {
        clearHoverClose();
        toggle(true);
    }
    else if (type === 'focusin' && triggers.includes('focus'))
    {
        clearHoverClose();
        toggle(true);
    }
    else if ((type === 'focusout' && (triggers.includes('focus') || triggers.includes('blur'))) || (type === 'mouseleave' && triggers.includes('hover')))
    {
        const eventTarget = e.target as HTMLElement;
        const relatedTarget = e.relatedTarget as HTMLElement;

        if (type === 'focusout' && pointerDownInsidePopover)
        {
            return;
        }

        if (isTransitionWithinPopoverBoundary(eventTarget, relatedTarget))
        {
            return;
        }

        if (type === 'mouseleave')
        {
            scheduleHoverClose();

            return;
        }

        toggle(false);
    }
};

function detachListeners(target?: HTMLElement | null)
{
    if (!target)
    {
        return;
    }

    const events = ['click', 'focusin', 'focusout', 'mouseenter', 'mouseleave'];

    for (const event of events)
    {
        target.removeEventListener(event, handleEvent as any, { capture: false });
    }
}

function attachListeners(target?: HTMLElement | null)
{
    if (!target)
    {
        return;
    }

    for (const trigger of resolvedTriggers.value)
    {
        if (trigger === 'click')
        {
            target.addEventListener('click', handleEvent, { capture: false });
        }
        else if (trigger === 'focus')
        {
            target.addEventListener('focusin', handleEvent, { capture: false });
            target.addEventListener('focusout', handleEvent, { capture: false });
        }
        else if (trigger === 'blur')
        {
            target.addEventListener('focusout', handleEvent, { capture: false });
        }
        else if (trigger === 'hover')
        {
            target.addEventListener('mouseenter', handleEvent, { capture: false });
            target.addEventListener('mouseleave', handleEvent, { capture: false });
        }
    }
}

function syncTarget()
{
    const target = targetElement();

    if (currentTarget.value === target)
    {
        return;
    }

    detachListeners(currentTarget.value);
    currentTarget.value = target;
    attachListeners(currentTarget.value);

    if (visible.value)
    {
        updatePosition(target);
    }
}

onMounted(() =>
{
    nextTick(() =>
    {
        if (!disposed)
            syncTarget();
    });

    document.addEventListener('pointerdown', handleDocumentPointerDown, { capture: true });
    document.addEventListener('mousedown', handleDocumentPointerDown, { capture: true });
    document.addEventListener('ideo-popover:open', onGlobalOpen);
});

onBeforeUnmount(() =>
{
    disposed = true;
    clearHoverClose();
    clearPointerDownInsidePopover();
    clearCloseAnimation();
    clearOpenAnimation();
    clearMenuContentAnimations();
    closeWhenReferenceUnavailable = null;
    rendered.value = false;
    visible.value = false;
});

onUnmounted(() =>
{
    detachListeners(currentTarget.value);
    currentTarget.value = null;

    document.removeEventListener('pointerdown', handleDocumentPointerDown, { capture: true });
    document.removeEventListener('mousedown', handleDocumentPointerDown, { capture: true });
    document.removeEventListener('ideo-popover:open', onGlobalOpen);
});

watch(() => props.target, async () =>
{
    await nextTick();
    if (!disposed)
        syncTarget();
});

defineExpose({
    open,
    close,
    element: () => popover.value
});
</script>

<template>
    <div
        v-if="rendered"
        ref="popover"
        v-bind="forwardedAttrs"
        class="ideo-floating"
        :class="popoverClasses"
        :style="popoverStyles"
        :data-dropdown-surface-enter-duration="props.variant === 'menu' ? 140 : undefined"
        :data-dropdown-surface-leave-duration="props.variant === 'menu' ? 140 : undefined"
        :data-dropdown-content-enter-duration="props.variant === 'menu' ? 140 : undefined"
        :data-dropdown-content-leave-duration="props.variant === 'menu' ? 140 : undefined"
        :data-dropdown-surface-enter-delay="props.variant === 'menu' ? 0 : undefined"
        :data-dropdown-surface-leave-delay="props.variant === 'menu' ? 35 : undefined"
        :data-dropdown-content-enter-delay="props.variant === 'menu' ? 35 : undefined"
        :data-dropdown-content-leave-delay="props.variant === 'menu' ? 0 : undefined"
        @pointerdown.capture="markPointerDownInsidePopover"
        @mousedown.capture="markPointerDownInsidePopover"
        @click.capture="handlePopoverClick"
    >
        <div v-if="arrow" ref="arrowRef" :class="arrowClasses" :style="arrowStyles" />
        <IdeoHeading v-if="title || 'title' in $slots" tag="h3" class="ideo-floating-header mb-[6px] text-[0.78125rem]/[1.3] font-[600] text-inherit">
            <slot name="title">
                {{ title }}
            </slot>
        </IdeoHeading>
        <div v-if="body" class="ideo-floating-body [overflow-wrap:anywhere]">
            <slot name="default" />
        </div>
        <slot v-else name="default" />
    </div>
</template>
